// ErrorBoundary.tsx — React class boundary catching render-time exceptions.
//
// Without this, any unhandled throw during render produces a blank white
// screen (React 18 unmounts the tree). User dogfood reported "白屏" on
// /sessions/{id}/report — this catches it and shows a readable fallback
// with a hard reload CTA + the captured error for triage.
//
// Use sparingly · wrap only the route-level components that mutate often
// (Report, Live) to avoid the full app blanking on a render bug.

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (err: Error, info: ErrorInfo | null) => ReactNode;
}

interface State {
  err: Error | null;
  info: ErrorInfo | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { err: null, info: null };

  static getDerivedStateFromError(err: Error): Partial<State> {
    return { err };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', err, info);
    this.setState({ err, info });
  }

  reset = () => this.setState({ err: null, info: null });

  render(): ReactNode {
    if (!this.state.err) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.state.err, this.state.info);
    return (
      <div
        className="max-w-2xl mx-auto px-8 py-16"
        role="alert"
        data-testid="error-boundary-fallback"
      >
        <h1 className="font-serif text-2xl font-medium mb-3">页面渲染异常</h1>
        <p className="text-ink-2 mb-4">
          这一页有一处渲染崩溃。已在浏览器 console 记录详细堆栈；请点下面的按钮回主控台或刷新重试。
        </p>
        <pre className="font-mono text-xs bg-paper-2 p-3 rounded mb-4 overflow-x-auto whitespace-pre-wrap">
          {this.state.err.name}: {this.state.err.message}
        </pre>
        <div className="flex gap-3">
          <a href="/dashboard" className="btn btn-primary">
            返回主控台
          </a>
          <button type="button" onClick={() => window.location.reload()} className="btn btn-secondary">
            刷新页面
          </button>
        </div>
      </div>
    );
  }
}
